'use client';

/**
 * ObeyaExplorerProvider — per-mode wrapper for the obeya explorer.
 *
 * Owns two pieces of state that the obeya panel needs and that the
 * generic schema-explorer can't model on its own:
 *
 *  1. `subMode` — "projects" | "simulations". Toggled by the
 *     `OBEYA_MODE_TOGGLE` section. Sibling sections gate their render
 *     on this state via `useObeyaExplorer()`.
 *
 *  2. The narrative-search context (query, isLoading, clarification,
 *     searchResults, projectContext, …). Wired to `ki_ask` exactly as
 *     the legacy `<SimulationsList />` was. Mounted ONLY when subMode
 *     is "simulations" so the generic search-flow sections (search_bar,
 *     project_card, clarification, search_results, empty_state) are
 *     auto-hidden on the projects side — `useExplorerSearch()` returns
 *     null with no provider, sections short-circuit.
 *
 * No business logic added or changed vs the legacy obeya-explorer; this
 * is a transcription of the existing `<SimulationsList />` flow into a
 * provider so the same UI runs through the schema-driven pipeline.
 *
 * @see klyde_edge/ui_config/entity_schema.py — obeya schema
 * @see klyde_ipe/components/explorer/sections/explorer-search-context.tsx
 *
 * @component_role: Explorer Provider
 * @component_type: React Context Provider
 * @domain: Frontend / Explorer / Obeya
 * @layer: presentation
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import {
  ExplorerSearchProvider,
  type ExplorerSearchContextValue,
  type ExplorerSearchResult,
  type ExplorerProjectContext,
} from '../sections/explorer-search-context';

export type ObeyaSubMode = 'projects' | 'simulations';

// ── ki_ask response shape (lifted from legacy obeya-explorer) ─────

interface AskResponse {
  status: 'simulation' | 'streaming' | 'search' | 'clarification' | 'error';
  intent: {
    entity_type: string | null;
    entity_hint: string | null;
    intent: string;
    confidence: string;
    reasoning: string;
  };
  simulation_id?: string;
  project?: {
    semantic_id: string;
    name: string;
    rag_status: string | null;
  };
  simulation?: {
    entity_sid: string;
    entity_type: string;
    entity_name: string | null;
    status: string;
  };
  search_results?: Array<{
    semantic_id: string;
    name: string;
    entity_type: string;
    state: string | null;
  }>;
  clarification?: string;
  error?: string;
}

// ── ObeyaExplorerContext (sub-mode toggle) ────────────────────────

interface ObeyaExplorerContextValue {
  subMode: ObeyaSubMode;
  setSubMode: (m: ObeyaSubMode) => void;
}

const ObeyaExplorerContext = createContext<ObeyaExplorerContextValue | null>(null);

export function useObeyaExplorer(): ObeyaExplorerContextValue {
  const ctx = useContext(ObeyaExplorerContext);
  if (!ctx) {
    throw new Error(
      'useObeyaExplorer must be used inside <ObeyaExplorerProvider /> — ' +
      'obeya sections require the provider to be mounted.',
    );
  }
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────

export function ObeyaExplorerProvider({ children }: { children: ReactNode }) {
  const [subMode, setSubMode] = useState<ObeyaSubMode>('projects');

  const toggleValue = useMemo<ObeyaExplorerContextValue>(
    () => ({ subMode, setSubMode }),
    [subMode],
  );

  return (
    <ObeyaExplorerContext.Provider value={toggleValue}>
      {subMode === 'simulations' ? (
        <ObeyaSimulationsSearchProvider>{children}</ObeyaSimulationsSearchProvider>
      ) : (
        children
      )}
    </ObeyaExplorerContext.Provider>
  );
}

// ── Simulations narrative-search wiring (ki_ask) ──────────────────

function ObeyaSimulationsSearchProvider({ children }: { children: ReactNode }) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setProjectContextStore = useGraphSelectionStore((s) => s.setProjectContext);
  const clearProjectStore = useGraphSelectionStore((s) => s.clearProject);
  const projectId = useGraphSelectionStore((s) => s.projectId);
  const projectContextStore = useGraphSelectionStore((s) => s.projectContext);

  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clarification, setClarification] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<ExplorerSearchResult[]>([]);

  const projectContext: ExplorerProjectContext | null = projectContextStore
    ? {
        semantic_id: projectId ?? '',
        name: projectContextStore.name,
        rag_status: projectContextStore.rag_status,
        rag_label: projectContextStore.rag_label,
        rfq_count: projectContextStore.rfq_count,
        po_count: projectContextStore.po_count,
      }
    : null;

  const runAsk = useCallback(
    async (q: string) => {
      setIsLoading(true);
      setError(null);
      setClarification(null);
      setSearchResults([]);

      try {
        const res = await api.getStrategyData<AskResponse>('ki_ask', { query: q });
        if (!res.success) throw new Error(res.error || 'ki_ask failed');
        const response = res.data;

        switch (response.status) {
          case 'simulation':
            if (response.simulation_id) {
              selectNode({
                id: response.simulation_id,
                type: 'simulation',
                label: response.simulation?.entity_name ?? 'Simulation',
              });
              if (response.project) {
                setProjectContextStore(response.project.semantic_id, {
                  name: response.project.name,
                  rag_status: response.project.rag_status ?? 'unknown',
                  rag_label: '',
                  rfq_count: 0,
                  po_count: 0,
                });
              }
              setQuery('');
            }
            break;

          case 'search':
            if (response.search_results?.length) {
              setSearchResults(response.search_results);
              if (response.clarification) setClarification(response.clarification);
            } else {
              setClarification(response.clarification || 'No matching entities found.');
            }
            break;

          case 'clarification':
            setClarification(response.clarification || 'Could you be more specific?');
            break;

          case 'error':
            setError(response.error || 'An error occurred');
            break;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process query');
      } finally {
        setIsLoading(false);
      }
    },
    [selectNode, setProjectContextStore],
  );

  const submit = useCallback(async () => {
    if (!query.trim()) return;
    await runAsk(query.trim());
  }, [query, runAsk]);

  const selectResult = useCallback(
    (result: ExplorerSearchResult) => {
      // Re-fire ki_ask using the picked entity's SID as the query —
      // matches the legacy `handleResultClick` behaviour.
      setSearchResults([]);
      setClarification(null);
      void runAsk(result.semantic_id);
    },
    [runAsk],
  );

  const hasContent =
    !!projectContext ||
    !!clarification ||
    searchResults.length > 0;

  const value: ExplorerSearchContextValue = {
    query,
    setQuery,
    submit,
    isLoading,
    error,
    clarification,
    searchResults,
    projectContext,
    selectResult,
    clearProject: clearProjectStore,
    hasContent,
  };

  return (
    <ExplorerSearchProvider value={value}>{children}</ExplorerSearchProvider>
  );
}
